import React, { useState } from 'react';
import { useConfirmationDialog } from '../providers/ConfirmationDialogProvider';
import {
    ListItem,
    ListItemText,
    IconButton,
    TextField,
    Box,
    Autocomplete,
    Chip,
    Button,
    Stack,
    Typography,
    Tooltip,
    LinearProgress
} from '@mui/material';
import { Edit, Close, Save, Delete } from '@mui/icons-material';
import BackupIcon from '@mui/icons-material/Backup';
import { useSnackbar } from "../providers/SnackbarProvider";
import { keyframes } from '@mui/material/styles';
import amplifiersProvider from '../providers/AmplifiersProvider';

const EditableListItem = ({
	amplifier: initialAmplifier,
	groups,
	token,
	onUpdateSuccess,
	onDelete,
	onEditingChange,
	globalLoading,
	setGlobalLoading,
	loadingItemId,
	setLoadingItemId
}) => {
	const { openSnackbar } = useSnackbar();
	const [amplifier, setAmplifier] = useState(initialAmplifier);
	const [isEditing, setIsEditing] = useState(false);
	const [name, setName] = useState(initialAmplifier.name || '');
	const [isSpecialEquipment] = useState(
		initialAmplifier.name === 'Central' ||
		initialAmplifier.name === 'Modem'
	);
	const [selectedGroup, setSelectedGroup] = useState(
		groups.find(g => g.vL_CADASTRO === initialAmplifier.groupId) || null
	);

	const pulseAnimation = keyframes`
		0% {
			border-color: ${theme => theme.palette.info.main};
			box-shadow: 0 0 0 0 rgba(41, 182, 246, 0.4);
		}
		50% {
			border-color: ${theme => theme.palette.info.light};
			box-shadow: 0 0 0 10px rgba(41, 182, 246, 0);
		}
		100% {
			border-color: ${theme => theme.palette.info.main};
			box-shadow: 0 0 0 0 rgba(41, 182, 246, 0);
		}
	`;

	const updateAmplifierName = async (updatedAmplifier) => {
		try {
			const response = await fetch(`${process.env.REACT_APP_URL}/amplifier/name`, {
				method: 'POST',
				headers: {
					"content-type": "application/json",
					"Authorization": "Bearer " + token
				},
				body: JSON.stringify(updatedAmplifier) // Envie o objeto diretamente
			});

			if (!response.ok) {
				const error = await response.json();
				throw Error(error.message || "Não foi possível atualizar o nome do amplificador");
			}

			const data = await response.json();

			return true;
		} catch (error) {
			console.error("Erro ao atualizar nome:", error);
			throw error;
		}
	};

	const updateAmplifierGroup = async (group, updatedAmplifier) => {
		try {
			const response = await fetch(`${process.env.REACT_APP_URL}/amplifier/update/group`, {
				method: 'POST',
				headers: {
					"content-type": "application/json",
					"Authorization": "Bearer " + token
				},
				body: JSON.stringify({
					group: group,
					amplifier: updatedAmplifier
				})
			});

			if (!response.ok) {
				throw Error("Não foi possível atualizar o grupo do amplificador");
			}

			return true;
		} catch (error) {
			console.error("Erro ao atualizar grupo:", error);
			throw error;
		}
	};

	const { openConfirmationDialog } = useConfirmationDialog();

	const handleDelete = () => {
		openConfirmationDialog({
			title: 'Excluir Dispositivo',
			message: `Deseja realmente excluir o dispositivo ${amplifier.serialNumber.toUpperCase()}?`,
			onConfirm: deleteAmplifier,
			confirmButtonColor: 'error'
		});
	};

	const handleEdit = () => {
		setIsEditing(true);
		onEditingChange(true);
	};

	// Função para iniciar loading apenas neste item
	const startLoading = async (action) => {
		setGlobalLoading(true);
		setLoadingItemId(amplifier.serialNumber);
		try {
			await action();
		} finally {
			setGlobalLoading(false);
			setLoadingItemId(null);
		}
	};

	const deleteAmplifier = async () => {
		await startLoading(async () => {
			await amplifiersProvider.queueDelete(amplifier.serialNumber);
			openSnackbar("Dispositivo excluído com sucesso!", "success");
			if (onDelete) onDelete(amplifier.serialNumber);
		});
	};

	const handleSave = async () => {
		await startLoading(async () => {
			await amplifiersProvider.updateAmplifier(amplifier.serialNumber, {
				name: name,
				group: selectedGroup
			});

			// Update local state
			const updatedAmplifier = {
				...amplifier,
				name: name,
				location: selectedGroup?.dS_LOCALIZACAO || '',
				groupId: selectedGroup?.vL_CADASTRO || ''
			};

			setAmplifier(updatedAmplifier);
			setIsEditing(false);
			onEditingChange(false);

			if (onUpdateSuccess) {
				onUpdateSuccess(updatedAmplifier);
			}

			openSnackbar("Amplificador atualizado com sucesso", "success");
		});
	};

	const handleCancel = () => {
		setName(amplifier.name || '');
		setSelectedGroup(groups.find(g => g.vL_CADASTRO === amplifier.groupId) || null);
		setIsEditing(false);
		onEditingChange(false);
	};

	// Exibe barra de progresso só no item em loading
	const isItemLoading = globalLoading && loadingItemId === amplifier.serialNumber;

	return (
		<ListItem
			elevation={2}
			sx={{
				bgcolor: '#212121',
				borderRadius: 1,
				mb: 1,
				display: 'flex',
				justifyContent: 'space-between',
				overflow: 'hidden',
				py: 1.5,
				px: 2,
				border: '2px solid',
				borderColor: isEditing
					? 'info.main'
					: (theme) => theme.palette.primary.main,
				borderTop: globalLoading ? "0px solid" : '2px solid',
				borderTopColor: isEditing
					? 'info.main'
					: (theme) => theme.palette.primary.main,
				transition: 'all 0.2s ease-in-out',
				animation: isEditing
					? `${pulseAnimation} 2s infinite ease-in-out`
					: 'none',
				'&:hover': {
					boxShadow: 3,
					borderColor: (theme) => theme.palette.info.main,
				}
			}}

            disabled={globalLoading}
		>
			{isItemLoading && (
				<LinearProgress
					color="secondary"
					sx={{
						width: '100%',
						position: 'absolute',
						top: 0,
						left: 0,
						borderTopLeftRadius: 1,
						borderTopRightRadius: 1
					}}
				/>
			)}
			{/* Coluna de Dados */}
			<Box sx={{
				display: 'grid',
				gridTemplateColumns: '1fr 1fr 120px',
				gap: 2,
				flex: 1,
				alignItems: 'center',
			}}>
				{/* DL e Nome */}
				<Box>
					{isEditing ? (
						<TextField
							fullWidth
							size="medium"
							label={`${amplifier.serialNumber.toUpperCase()} - Nome`}
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="Digite um novo nome"
							disabled={globalLoading}
						/>
					) : (
						<Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
								<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
									{amplifier.serialNumber.toUpperCase()}
								</Typography>
								{amplifier.network.dhAddress.includes("a201") && (
									<Chip
										label=""
										color="warning"
										size="small"
										sx={{
											width: '16px',
											height: '16px',
											padding: 0,
											'& .MuiChip-label': {
												display: 'none'
											}
										}}
									/>
								)}
							</Box>
							<Typography variant="body2" color="text.secondary">
								{amplifier.name || 'Sem Nome'}
							</Typography>
						</Box>
					)}
				</Box>

				{/* Grupo */}
				<Box>
					{amplifier.name !== 'Central' && amplifier.name !== 'Modem' ?
						(isEditing ? (
							<Autocomplete
								size="medium"
								options={groups.sort((a, b) => {
									const numA = parseInt(a.vL_CADASTRO.slice(12));
									const numB = parseInt(b.vL_CADASTRO.slice(12));
									return numA - numB;
								})}
								value={selectedGroup}
								onChange={(_, newValue) => setSelectedGroup(newValue)}
								getOptionLabel={(option) =>
									`${option.vL_CADASTRO.slice(12)} - ${option.dS_LOCALIZACAO || 'Sem nome registrado'}`
								}
								renderInput={(params) => (
									<TextField {...params} label={`${amplifier.serialNumber.toUpperCase()} - Grupo`} />
								)}
								disableClearable={true}
								disabled={globalLoading}
							/>
						) : (
							<Box>
								<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
									Grupo:
								</Typography>
								<Typography variant="body2">
									{`${amplifier.groupId ? amplifier.groupId.slice(12) : ''} - ${amplifier.location || 'Sem nome registrado'}`}
								</Typography>

							</Box>
						)
						) : (
							<Box></Box>
						)
					}
				</Box>

				{/* Status Badge */}
				<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
					<Chip
						label={amplifier?.serialNumber?.includes("a201") ? "não mapeado" : amplifier.status}
						color={
							amplifier?.serialNumber?.includes("a201") ? 'warning' :
								amplifier.status === 'online' ? 'success' :
									amplifier.status === 'offline' ? 'error' :
										amplifier.status === 'buscando' ? 'info' : 'warning'
						}
						size="small"
					/>
				</Box>
			</Box>

			{/* Coluna de Ações */}
			<Box sx={{
				display: 'flex',
				gap: 1,
				ml: 2,
				borderLeft: 1,
				borderColor: 'divider',
				pl: 2
			}}>
				{isEditing ? (
					<>
						<Tooltip title="Salvar alterações">
							<IconButton
								onClick={handleSave}
								color="primary"
								size="small"
								disabled={globalLoading}>
								<Save />
							</IconButton>
						</Tooltip>
						<Tooltip title="Cancelar edição">
							<IconButton
								onClick={handleCancel}
								color="error"
								size="small"
								disabled={globalLoading}>
								<Close />
							</IconButton>
						</Tooltip>
					</>
				) : (
					<>
						<Tooltip title={isSpecialEquipment ? "Não é possível editar este dispositivo" : "Editar dispositivo"}>
							<span>
								<IconButton
									onClick={handleEdit}
									color="primary"
										size="small"
										disabled={isSpecialEquipment || globalLoading}
									sx={{ opacity: isSpecialEquipment ? 0.5 : 1 }}>
									<Edit />
								</IconButton>
							</span>
						</Tooltip>

						<Tooltip title="Atualizar em Nuvem">
							<IconButton
								onClick={handleSave}
								color="primary"
								size="small"
								disabled={globalLoading}>
								<BackupIcon />
							</IconButton>
						</Tooltip>

						<Tooltip title="Excluir dispositivo">
							<IconButton
								onClick={handleDelete}
								color="error"
								size="small"
								disabled={globalLoading}>
								<Delete />
							</IconButton>
						</Tooltip>
					</>
				)}
			</Box>
		</ListItem>
	);
};

export default EditableListItem;